"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useStableRowsDuringEdit = void 0;
const react_1 = require("react");
const ramda_1 = require("ramda");
const isOptimisticUpdate = (update) => ('row' in update && 'commit' in update);
const getChangedField = (newRow, oldRow) => (Object.keys(newRow).find((key) => !(0, ramda_1.equals)(newRow[key], oldRow[key])));
/**
 * Freezes displayed rows while a cell is in edit mode so that background
 * refetches don't re-sort the DataGrid mid-edit. Both handleCellEditStart
 * and handleCellEditStop must be wired to the DataGrid.
 */
const useStableRowsDuringEdit = ({ rows, processRowUpdate, onCellEditStart, shouldHoldDisplayedRows = false, }) => {
    const editSessionRef = (0, react_1.useRef)(0);
    const isProcessingRef = (0, react_1.useRef)(false);
    const latestRowsRef = (0, react_1.useRef)(rows);
    const rowUpdateVersionsRef = (0, react_1.useRef)(new Map());
    const optimisticRowsRef = (0, react_1.useRef)(new Map());
    const [displayedRows, setDisplayedRows] = (0, react_1.useState)(rows);
    const applyOptimisticRows = (0, react_1.useCallback)((nextRows) => (nextRows.map((row) => { var _a; return (_a = optimisticRowsRef.current.get(row.id)) !== null && _a !== void 0 ? _a : row; })), []);
    (0, react_1.useEffect)(() => {
        if (shouldHoldDisplayedRows && latestRowsRef.current.length > 0) {
            return;
        }
        const nextRows = applyOptimisticRows(rows);
        latestRowsRef.current = nextRows;
        if (editSessionRef.current === 0) {
            setDisplayedRows(nextRows);
        }
    }, [applyOptimisticRows, rows, shouldHoldDisplayedRows]);
    const applyRowUpdate = (0, react_1.useCallback)((updatedRow) => {
        const nextDisplayedRows = latestRowsRef.current.map((row) => (row.id === updatedRow.id ? updatedRow : row));
        latestRowsRef.current = nextDisplayedRows;
        setDisplayedRows(nextDisplayedRows);
    }, []);
    const patchDisplayedRow = (0, react_1.useCallback)((rowId, patch) => {
        const row = latestRowsRef.current.find(({ id }) => id === rowId);
        if (!row)
            return;
        applyRowUpdate(Object.assign(Object.assign({}, row), patch));
    }, [applyRowUpdate]);
    const createRowUpdateVersion = (0, react_1.useCallback)((rowId) => {
        var _a;
        const nextVersion = ((_a = rowUpdateVersionsRef.current.get(rowId)) !== null && _a !== void 0 ? _a : 0) + 1;
        rowUpdateVersionsRef.current.set(rowId, nextVersion);
        return nextVersion;
    }, []);
    const applyOptimisticRowUpdate = (0, react_1.useCallback)((updatedRow) => {
        optimisticRowsRef.current.set(updatedRow.id, updatedRow);
        applyRowUpdate(updatedRow);
    }, [applyRowUpdate]);
    const clearOptimisticRowUpdate = (0, react_1.useCallback)((rowId) => {
        optimisticRowsRef.current.delete(rowId);
    }, []);
    const getRowsForUpdate = (0, react_1.useCallback)((newRow, oldRow) => {
        const localOldRow = latestRowsRef.current.find(({ id }) => id === oldRow.id);
        if (!localOldRow) {
            return { newRow, oldRow };
        }
        const changedField = getChangedField(newRow, oldRow);
        return {
            oldRow: localOldRow,
            newRow: changedField
                ? Object.assign(Object.assign({}, localOldRow), { [changedField]: newRow[changedField] }) : newRow,
        };
    }, []);
    const handleCellEditStart = (0, react_1.useCallback)((params) => {
        editSessionRef.current += 1;
        onCellEditStart === null || onCellEditStart === void 0 ? void 0 : onCellEditStart(params.id, params.field);
    }, [onCellEditStart]);
    const handleCellEditStop = (0, react_1.useCallback)(() => {
        const session = editSessionRef.current;
        // Deferred: let stabilizedProcessRowUpdate claim the session first (MUI
        // calls it synchronously after onCellEditStop). Unfreeze only if no
        // processRowUpdate followed and no new edit session started.
        queueMicrotask(() => {
            if (session === editSessionRef.current && !isProcessingRef.current) {
                editSessionRef.current = 0;
                setDisplayedRows(latestRowsRef.current);
            }
        });
    }, []);
    const stabilizedProcessRowUpdate = (0, react_1.useCallback)((newRow, oldRow) => __awaiter(void 0, void 0, void 0, function* () {
        isProcessingRef.current = true;
        try {
            const rowsForUpdate = getRowsForUpdate(newRow, oldRow);
            const update = yield processRowUpdate(rowsForUpdate.newRow, rowsForUpdate.oldRow);
            if (isOptimisticUpdate(update)) {
                const rowVersion = createRowUpdateVersion(update.row.id);
                applyOptimisticRowUpdate(update.row);
                void update.commit
                    .then((serverRow) => {
                    if (rowUpdateVersionsRef.current.get(update.row.id) === rowVersion) {
                        clearOptimisticRowUpdate(update.row.id);
                        applyRowUpdate(serverRow);
                    }
                })
                    .catch((error) => {
                    var _a;
                    if (rowUpdateVersionsRef.current.get(update.row.id) === rowVersion) {
                        if (update.rollbackRow) {
                            clearOptimisticRowUpdate(update.row.id);
                            applyRowUpdate(update.rollbackRow);
                        }
                        (_a = update.onError) === null || _a === void 0 ? void 0 : _a.call(update, error);
                    }
                });
                return update.row;
            }
            createRowUpdateVersion(update.id);
            clearOptimisticRowUpdate(update.id);
            applyRowUpdate(update);
            return update;
        }
        finally {
            isProcessingRef.current = false;
            editSessionRef.current = 0;
        }
    }), [
        applyOptimisticRowUpdate,
        applyRowUpdate,
        clearOptimisticRowUpdate,
        createRowUpdateVersion,
        getRowsForUpdate,
        processRowUpdate,
    ]);
    return {
        displayedRows,
        handleCellEditStart,
        handleCellEditStop,
        patchDisplayedRow,
        stabilizedProcessRowUpdate,
    };
};
exports.useStableRowsDuringEdit = useStableRowsDuringEdit;
